/**
 * AI'ın ürettiği HTML raporun kartı ve önizlemesi.
 *
 * Rapor sohbet balonunun içine gömülmüyor: uzun bir belge, konuşmayı okunmaz hale
 * getiriyor. Kart raporun adını ve boyutunu gösteriyor; açınca tam ekran önizleme
 * geliyor (mobilde de tam ekran — rapor dar bir panelde okunmaz).
 *
 * Önizleme SANDBOX'lı iframe'de: içerik modelden geliyor ve uygulamanın DOM'una,
 * çerezlerine erişmemeli. `allow-same-origin` BİLİNÇLİ verilmiyor; yazdırma köprüsü
 * için yalnız `allow-scripts` var (Razor şablon penceresiyle aynı yaklaşım).
 */
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileText, Maximize2, Printer } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { Dialog } from '@/shared/ui/Dialog';
import type { ReportArtifact } from '@/shared/types/aiChat';
import { downloadHtml } from '../model/artifacts';

/** Yazdırma köprüsü: dışarıdan gelen postMessage ile iframe kendini yazdırır. */
const PRINT_MESSAGE = 'octo-ai-print';

function buildSrcDoc(html: string): string {
  // Kapanış etiketi parçalı yazılıyor: kaynak dosyadaki `</script>` tarayıcıya erken
  // kapanış gibi görünmesin.
  const bridge =
    `<script>window.addEventListener('message',function(e){` +
    `if(e.data===${JSON.stringify(PRINT_MESSAGE)}){window.focus();window.print();}});` +
    `</scr` +
    `ipt>`;
  // Belge tam bir HTML sayfasıysa köprü </body>'den önce, değilse sonuna eklenir.
  return /<\/body>/i.test(html) ? html.replace(/<\/body>/i, `${bridge}</body>`) : `${html}${bridge}`;
}

export function AiArtifactCard({ artifact }: { artifact: ReportArtifact }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const srcDoc = useMemo(() => (open ? buildSrcDoc(artifact.code) : ''), [open, artifact.code]);
  const sizeKb = Math.max(1, Math.round(artifact.code.length / 1024));

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="border-border bg-bg-elevated hover:border-brand-500/60 hover:bg-bg-hover mt-2 flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors"
      >
        <span className="bg-brand-500/10 text-brand-600 flex h-9 w-9 shrink-0 items-center justify-center rounded-md">
          <FileText className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{artifact.title}</span>
          <span className="text-fg-subtle block text-xs">
            {t('ai.artifact.open')} · {sizeKb} KB
          </span>
        </span>
        <Maximize2 className="text-fg-subtle h-4 w-4 shrink-0" />
      </button>

      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={artifact.title}
        size="full"
        fill
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => frameRef.current?.contentWindow?.postMessage(PRINT_MESSAGE, '*')}
            >
              <Printer className="h-4 w-4" />
              {t('ai.artifact.print')}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => downloadHtml(artifact)}>
              <Download className="h-4 w-4" />
              {t('ai.artifact.download')}
            </Button>
            <Button size="sm" onClick={() => setOpen(false)}>
              {t('common:Close')}
            </Button>
          </>
        }
      >
        <iframe
          ref={frameRef}
          title={artifact.title}
          srcDoc={srcDoc}
          // allow-same-origin YOK: rapor opak kaynakta kalır, uygulamaya erişemez.
          sandbox="allow-scripts allow-modals allow-popups"
          referrerPolicy="no-referrer"
          className="border-border bg-bg h-full min-h-[60vh] w-full rounded-md border"
        />
      </Dialog>
    </>
  );
}
